<script setup lang="ts">
    const { progress, isLoading, error } = useLoadingIndicator({ throttle: 0 });
    const { t: translate } = useI18n();
    const loadingMessage = ref('');

    watch(isLoading, (loading) => {
        // Keep the announcement stable if the locale changes during navigation.
        loadingMessage.value = loading ? translate('general.pageLoading') : '';
    });
</script>

<template>
    <div
        class="page-loading-indicator"
        :class="{ '--loading': isLoading, '--error': error }"
        :style="{ '--page-loading-indicator-progress': progress / 100 }"
        aria-hidden="true"
    />

    <!-- Keep this live region mounted so screen readers detect content changes. -->
    <span
        class="sr-only"
        role="status"
        aria-atomic="true"
    >
        {{ loadingMessage }}
    </span>
</template>

<style scoped>
    .page-loading-indicator {
        --page-loading-indicator-height: 4px;
        --page-loading-indicator-progress-duration: 0.1s;
        --page-loading-indicator-fade-duration: 0.4s;
        --page-loading-indicator-layer: 999999;

        /* Preserve Nuxt's default error colors. */
        --page-loading-indicator-error-gradient: repeating-linear-gradient(to right, #f87171 0%, #ef4444 100%);
        --page-loading-indicator-gradient: linear-gradient(
            to right,
            rgb(from var(--page-loading-indicator-outer) r g b / 0) 0%,
            var(--page-loading-indicator-outer) 16.19%,
            var(--page-loading-indicator-center) 40.84%,
            var(--page-loading-indicator-center) 57.6%,
            var(--page-loading-indicator-outer) 85.25%,
            rgb(from var(--page-loading-indicator-outer) r g b / 0) 100%
        );

        position: fixed;
        top: 0;
        right: 0;
        left: 0;
        z-index: var(--page-loading-indicator-layer);
        height: var(--page-loading-indicator-height);
        pointer-events: none;
        opacity: 0;
        background-image: var(--page-loading-indicator-gradient);
        background-size: 100% 100%;
        transform: scaleX(var(--page-loading-indicator-progress));
        transform-origin: center;
        transition:
            transform var(--page-loading-indicator-progress-duration),
            opacity var(--page-loading-indicator-fade-duration);
    }

    .page-loading-indicator.--loading {
        opacity: 1;
    }

    .page-loading-indicator.--error {
        background-image: var(--page-loading-indicator-error-gradient);
    }

    @media (prefers-reduced-motion: reduce) {
        .page-loading-indicator {
            transform: none;
            background-position: center;
            transition: none;
            animation: none;
        }
    }
</style>
